import { useSnackbarController } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdminModal,
  adminModalInputClass,
  adminModalLabelClass,
} from "../custom/AdminModal";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import {
  NOTIFICATION_TYPE_LABELS,
  type NotificationType,
  type SendableGeneralNotificationType,
} from "../collections/notifications";
import { getModeCollectionIds } from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";

type DefOption = {
  id: string;
  type: SendableGeneralNotificationType;
  label: string;
};

type ListResponse = {
  defs: Array<{ id: string; type: SendableGeneralNotificationType }>;
};

export function SendGeneralNotificationButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDefs, setLoadingDefs] = useState(false);
  const [options, setOptions] = useState<DefOption[]>([]);
  const [selectedDefId, setSelectedDefId] = useState("");
  const { mode } = useAdminWorkspace();
  const workspaceIds = useMemo(() => getModeCollectionIds(mode), [mode]);
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();

  const accountId = workspaceIds.account?.accountId;
  const eventId = workspaceIds.event?.eventId;
  const eventReady = Boolean(accountId && eventId);

  const loadDefs = useCallback(async () => {
    if (!functions || !accountId || !eventId) {
      return;
    }
    setLoadingDefs(true);
    try {
      const list = httpsCallable<
        { accountId: string; eventId: string },
        ListResponse
      >(functions, "listSendableNotificationDefs");
      const result = await list({ accountId, eventId });
      const next = (result.data.defs ?? []).map((def) => ({
        id: def.id,
        type: def.type,
        label: NOTIFICATION_TYPE_LABELS[def.type as NotificationType] ?? def.type,
      }));
      next.sort((a, b) => a.label.localeCompare(b.label));
      setOptions(next);
      setSelectedDefId(next[0]?.id ?? "");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load notification templates";
      snackbarController.open({ type: "error", message });
    } finally {
      setLoadingDefs(false);
    }
  }, [functions, accountId, eventId, snackbarController]);

  useEffect(() => {
    if (open) {
      void loadDefs();
    }
  }, [open, loadDefs]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setOpen(false);
      setSelectedDefId("");
    }
  }, [loading]);

  const handleSubmit = useCallback(async () => {
    if (!functions || !accountId || !eventId) {
      snackbarController.open({
        message: "Select an active event first",
        type: "warning",
      });
      return;
    }
    if (!selectedDefId) {
      snackbarController.open({
        message: "Select a notification to send",
        type: "warning",
      });
      return;
    }

    const callable = httpsCallable(functions, "sendEventGeneralNotification");
    try {
      setLoading(true);
      await callable({ accountId, eventId, defId: selectedDefId });
      snackbarController.open({
        type: "success",
        message: "Notification sent",
      });
      setOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to send notification";
      snackbarController.open({ type: "error", message });
    } finally {
      setLoading(false);
    }
  }, [functions, accountId, eventId, selectedDefId, snackbarController]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={!eventReady}
        onClick={() => setOpen(true)}
      >
        Send notification
      </Button>
      <AdminModal
        open={open}
        onClose={handleClose}
        ariaLabel="Send general notification"
        title="Send general notification"
        description="Pick a notification template. Unit unlocked is automatic and does not appear here."
        footer={
          <>
            {loading && <CircularProgress size="small" />}
            <Button onClick={handleClose} disabled={loading} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={loading || !selectedDefId}
              onClick={() => void handleSubmit()}
              variant="outlined"
            >
              Send now
            </Button>
          </>
        }
      >
        <label className={adminModalLabelClass}>Notification</label>
        <select
          className={adminModalInputClass}
          value={selectedDefId}
          disabled={loading || loadingDefs || options.length === 0}
          onChange={(event) => setSelectedDefId(event.target.value)}
        >
          {options.length === 0 ? (
            <option value="">
              {loadingDefs
                ? "Loading…"
                : "No sendable defs (configure under account)"}
            </option>
          ) : (
            options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))
          )}
        </select>
      </AdminModal>
    </>
  );
}
